import { useState } from 'react'
import { useDispatch } from 'react-redux'
import cn from 'clsx'

// Actions
import { actions as trackingActions } from 'modules/tracking/actions'

// Components
import { Checklist } from 'kit'
import { Button, Checkbox, Input, Toast, IconFileText } from '@tracklib/kit'
import PhoneInput from 'components/phoneinput/phoneinput'

// Types
import type { UserSong, Song } from 'types'
import type { LicenseSelectorInfo } from 'selectors'

import styles from './details.css'

type DetailsProps = {
  userSong: UserSong
  song: Song
  licenseInfo: LicenseSelectorInfo
  licensee: string
  phone: number | string
  onChangePhone: (input: string) => unknown
  isLoading: boolean
  isDone: boolean
  onChangeLicensee: (e: React.ChangeEvent<HTMLInputElement>) => void
  onClick: () => void
  country?: string
}

const Details = ({
  song,
  licensee,
  phone,
  onChangePhone,
  isDone,
  onChangeLicensee,
  onClick,
  isLoading,
  country,
  userSong,
  licenseInfo,
}: DetailsProps) => {
  const dispatch = useDispatch()
  const [agreement, setAgreement] = useState(false)
  const [agreementTwo, setAgreementTwo] = useState(false)
  return (
    <>
      <div className={cn(styles.box, styles.borderBox)}>
        <h1 className={styles.title}>About the Tracklib sample license</h1>
        <p className={styles.subtitle}>
          Here is a summary of some of the key things you can, cannot and must do with your new song once it’s licensed but please read the
          whole license carefully (see link below) as it contains important additional information.
        </p>
        <Toast
          className={styles.toast}
          description="Please note that after you’ve obtained your license there are a number of important steps you need to take to activate your license."
        />
        <Checklist.License licenseInfo={licenseInfo} syncAllowed={song.licenseinfo.allow_sync} />
        <hr className={styles.divider} />
        <Button
          to="previewlicense"
          modal
          query={{ userSongId: userSong.id }}
          variant="transparent"
          onClick={() =>
            dispatch(
              trackingActions.trackevent('Previewed license', {
                userSongId: userSong.id,
              }),
            )
          }
          datatesting="button_preview_license"
          className={styles.previewButton}
        >
          <IconFileText className={styles.iconFile} /> Click here to preview the full license
        </Button>
      </div>

      <div className={styles.box}>
        <h2 className={styles.title}>Complete the agreement</h2>
        <Input
          label="Name of licensee"
          name="licensee"
          value={licensee}
          onChange={onChangeLicensee}
          placeholder="Your full name or company name"
          autoComplete={false}
        />
        <PhoneInput
          label="Phone number"
          name="phone_number"
          value={phone}
          onChange={onChangePhone}
          className={styles.input}
          placeholder="+0 000 000 0000"
          defaultCountry={country}
          description="Including country code"
        />
        <Checkbox
          name="confirm"
          onChange={() => setAgreement(!agreement)}
          value={agreement}
          bordered
          size="large"
          type="yellow"
          className={styles.checkbox}
          disabled={!licensee || !phone}
          datatesting="checkbox_confirm"
        >
          I confirm that I am about to enter into the Tracklib sample license either on my own behalf or on behalf of
          {licensee && (
            <>
              , <strong>{licensee}</strong>,{' '}
            </>
          )}{' '}
          and that I am fully authorized to do so.
        </Checkbox>
        <Checkbox
          name="confirm"
          onChange={() => setAgreementTwo(!agreementTwo)}
          value={agreementTwo}
          bordered
          size="large"
          type="yellow"
          className={styles.checkbox}
          disabled={!licensee || !phone}
          datatesting="checkbox_understand"
        >
          I also understand and agree that by making payment of the license fee I am entering into a binding legal agreement and agreeing to
          its terms and conditions.
        </Checkbox>
        <Button
          onClick={onClick}
          disabled={!licensee || !agreement || !agreementTwo || isLoading}
          isLoading={isLoading}
          isDone={isDone}
          datatesting="continuetopaymentbutton"
        >
          Continue to payment of license fee
        </Button>
      </div>
    </>
  )
}

export default Details
